import { useCallback, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface NoteCellProps {
  note: string | null | undefined;
  /**
   * Opens the หมายเหตุ dialog for adding or editing the text. Without it the cell only reads:
   * no เพิ่มหมายเหตุ and no แก้ไข.
   */
  onEdit?: () => void;
  /**
   * An orange line for something still unexplained: on its own in a read-only cell with no
   * note, under the note when there is one.
   */
  pendingText?: string;
  /** For a table with more columns to fit than รายงานการโอน. */
  maxWidth?: number;
}

/** Lines shown before the note is collapsed behind "ดูเพิ่มเติม". */
const CLAMP_LINES = 2;
const MIN_WIDTH = 160;
const MAX_WIDTH = 280;

const linkSx = {
  color: "link.main",
  cursor: "pointer",
} as const;

const PENDING_SX = { fontWeight: 500 } as const;

/**
 * หมายเหตุ rendered inline. The note ships with every list row, so the text is displayed
 * directly rather than behind a dialog.
 *
 * The server accepts up to 2000 characters (the BO dialog caps its own input at 500, but the
 * PWA writes to the same column) and the text may contain newlines, so it is clamped to two
 * lines and expanded on demand — an unclamped note would blow up the row height for the
 * whole table.
 *
 * Overflow is measured rather than assumed: the toggle only appears when the text is actually
 * taller than the clamp, so a short note does not get a pointless "ดูเพิ่มเติม".
 */
export default function NoteCell({
  note,
  onEdit,
  pendingText,
  maxWidth = MAX_WIDTH,
}: NoteCellProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);
  const trimmedNote = note?.trim();

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) {
      setIsOverflowing(false);
      return;
    }
    setIsOverflowing(element.scrollHeight - element.clientHeight > 1);
  }, [trimmedNote]);

  const toggleExpanded = useCallback(
    () => setIsExpanded((previous) => !previous),
    [],
  );

  const pendingLine = pendingText ? (
    <Typography variant="body2" color="warning.dark" sx={PENDING_SX}>
      {pendingText}
    </Typography>
  ) : null;

  if (!trimmedNote) {
    if (!onEdit) return pendingLine;
    return (
      <Link
        component="button"
        type="button"
        onClick={onEdit}
        underline="always"
        sx={{ ...linkSx, fontSize: "0.8125rem", fontWeight: 400 }}
      >
        เพิ่มหมายเหตุ
      </Link>
    );
  }

  const clampSx = isExpanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: CLAMP_LINES,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  const canToggle = isOverflowing || isExpanded;
  const hasLinks = canToggle || Boolean(onEdit);

  return (
    <Box sx={{ minWidth: MIN_WIDTH, maxWidth, textAlign: "left" }}>
      <Typography
        ref={textRef}
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", ...clampSx }}
      >
        {trimmedNote}
      </Typography>

      {hasLinks && (
        <Stack direction="row" gap={1.5} sx={{ mt: 0.25 }}>
          {canToggle && (
            <Link
              component="button"
              type="button"
              onClick={toggleExpanded}
              underline="always"
              sx={{ ...linkSx, fontSize: "0.75rem" }}
            >
              {isExpanded ? "ย่อ" : "ดูเพิ่มเติม"}
            </Link>
          )}
          {onEdit && (
            <Link
              component="button"
              type="button"
              onClick={onEdit}
              underline="always"
              sx={{ ...linkSx, fontSize: "0.75rem" }}
            >
              แก้ไข
            </Link>
          )}
        </Stack>
      )}
      {pendingLine}
    </Box>
  );
}
